import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, MessageCircle, Phone } from "lucide-react";

const navItems = [
  { label: "שירותים", href: "/services" },
  { label: "אודות", href: "/about" },
  { label: "גלריה", href: "/gallery" },
  { label: "צור קשר", href: "/contact" },
];

const whatsappNumber = "972545725681";
const whatsappMessage = encodeURIComponent("שלום, אשמח לקבל פרטים נוספים על שירותי אור בגג");
const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const renderNavLink = (
    item: { label: string; href: string },
    className: string,
    onClick?: () => void
  ) => {
    if (item.href.startsWith("/") && !item.href.includes("#")) {
      return (
        <Link key={item.href} to={item.href} onClick={onClick} className={className}>
          {item.label}
        </Link>
      );
    }
    const href =
      location.pathname !== "/" && item.href.startsWith("/#")
        ? item.href
        : item.href.replace("/#", "#");
    return (
      <a key={item.href} href={href} onClick={onClick} className={className}>
        {item.label}
      </a>
    );
  };

  return (
    <>
      <header className="fixed top-0 right-0 left-0 z-50 bg-dark border-b border-dark/10">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <Link to="/" className="text-2xl font-bold text-primary">
            אור בגג
          </Link>

          <nav className="hidden md:flex items-center gap-8" aria-label="ניווט ראשי">
            {navItems.map((item) =>
              renderNavLink(item, "text-dark-foreground hover:text-primary transition-colors font-medium")
            )}
          </nav>

          <Button
            asChild
            className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-gold-light font-bold"
          >
            <a href="/#contact">תיאום ביקור מקצועי</a>
          </Button>

          {/* Mobile trigger */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="פתח תפריט"
            aria-expanded={open}
            className="md:hidden relative w-11 h-11 flex items-center justify-center rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Fullscreen mobile menu */}
      <div
        className={`md:hidden fixed inset-0 z-[60] transition-all duration-500 ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
        aria-hidden={!open}
      >
        {/* Background */}
        <div className="absolute inset-0 bg-dark">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, hsl(var(--primary) / 0.25), transparent 40%), radial-gradient(circle at 80% 80%, hsl(var(--primary) / 0.18), transparent 45%)",
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative h-full flex flex-col">
          {/* Top bar */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-primary/10">
            <span className="text-2xl font-bold text-primary">אור בגג</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="סגור תפריט"
              className="w-11 h-11 flex items-center justify-center rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav links */}
          <nav
            className="flex-1 flex flex-col justify-center px-8 gap-2 overflow-y-auto"
            aria-label="ניווט ראשי במובייל"
          >
            {navItems.map((item, i) => (
              <div
                key={item.href}
                className={`transform transition-all duration-500 ${
                  open ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
                }`}
                style={{ transitionDelay: open ? `${120 + i * 80}ms` : "0ms" }}
              >
                {renderNavLink(
                  item,
                  "group flex items-baseline justify-between py-4 border-b border-primary/10 text-dark-foreground hover:text-primary transition-colors",
                  () => setOpen(false)
                )}
                {/* Re-render with rich content via wrapper trick */}
              </div>
            ))}

            {/* Decorative number list overlay (visual rich style) */}
            <style>{`
              .menu-link-num { display: none; }
            `}</style>
          </nav>

          {/* Bottom CTAs */}
          <div
            className={`px-6 pb-8 pt-4 space-y-3 transform transition-all duration-500 ${
              open ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
            }`}
            style={{ transitionDelay: open ? `${120 + navItems.length * 80}ms` : "0ms" }}
          >
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-3 w-full h-14 rounded-full font-bold text-white shadow-lg active:scale-[0.98] transition-transform"
              style={{ backgroundColor: "#25D366" }}
            >
              <MessageCircle className="w-5 h-5" fill="white" strokeWidth={0} />
              שלחו הודעת וואטסאפ
            </a>
            <a
              href="tel:+972545725681"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-3 w-full h-12 rounded-full border border-primary/40 text-primary font-medium hover:bg-primary/10 transition-colors"
            >
              <Phone className="w-4 h-4" />
              חייגו עכשיו
            </a>
            <p className="text-center text-xs text-dark-foreground/50 pt-2">
              13 שנות ניסיון · אחריות מלאה
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
